/**
 * Integrytas Group - Base App (MOBILE)
 * Page: DMS.DOCUMENTS — mobile-native.
 *
 * Touch-first counterpart of src/pages/dms/DmsDocumentsPage.tsx. The desktop page
 * is a generic console: an operator points <DocumentManager> at any polymorphic
 * target (entity name + record id). The mobile presentation flips that into a
 * scannable, phone-native list of the latest document of every attached family
 * (the `dms_document` rows where is_latest='Y'), with a bottom-sheet detail that
 * surfaces the document's metadata + status. It shares the DATA/LOGIC layer only:
 *   - reads via apiDb (same dms_document table the desktop DmsService reads),
 *     resolving uploaded_by_user_id to the uploader's name so no raw id reaches
 *     render (§17.B4),
 *   - the archive/restore write (is_active toggle) routes through the Common
 *     Routine via routedUpdate — §2 single write path; never a direct apiDb write.
 * It never re-renders the desktop grid; §16. Versioning, upload and revert stay on
 * the desktop DocumentManager for now.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, X, FileText, Archive, ArchiveRestore, Eye } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { fetchAllRows } from '../../lib/fetch_all_rows';
import { useFormatDateTime } from '../../lib/format_date';
import { routedUpdate } from '../../lib/routed_writes';
import { getSignedUrl } from '../../services/dms/DmsService';
import {
  MobilePageHeader, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips,
  MobileStatusPill,
} from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'DMS.DOCUMENTS';

interface MobileDocRow {
  document_id: number;
  version_number: number;
  is_active: 'Y' | 'N';
  target_entity_name: string;
  target_record_id: string;
  original_name: string;
  file_type: string | null;
  file_size: number | null;
  storage_path: string;
  upload_timestamp: string;
  uploaded_by_name: string;
}

type StatusFacet = 'all' | 'active' | 'archived';

function formatBytes(bytes: number | null): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

export function MobileDmsDocumentsPage({ session }: MobilePageComponentProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const formatDateTime = useFormatDateTime();
  const [docs, setDocs] = useState<MobileDocRow[]>([]);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('active');
  const [selected, setSelected] = useState<MobileDocRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      // Latest version of every distinct document family (active + archived so the
      // status facet can surface both), newest upload first.
      const rawDocs = await fetchAllRows((from, to) => apiDb
        .from('dms_document')
        .select(
          'document_id, version_number, is_active, target_entity_name, target_record_id, original_name, file_type, file_size, storage_path, uploaded_by_user_id, upload_timestamp',
        )
        .eq('is_latest', 'Y')
        .order('upload_timestamp', { ascending: false }).order('document_id').range(from, to));

      type RawDoc = Omit<MobileDocRow, 'uploaded_by_name'> & { uploaded_by_user_id: number | null };
      const rows = rawDocs as RawDoc[];

      // Resolve the uploader FK to a display name — never render the raw id (§17.B4).
      // Scoped to only the uploaders on this page, never a full user-table preload.
      const uploaderIds = [...new Set(rows.map(r => r.uploaded_by_user_id).filter((v): v is number => v != null))];
      const rawUsers = uploaderIds.length
        ? await fetchAllRows((from, to) => apiDb.from('csm_user_master').select('user_id, display_name, email').in('user_id', uploaderIds).order('user_id').range(from, to))
        : [];
      const userNames: Record<number, string> = {};
      for (const u of rawUsers as { user_id: number; display_name: string | null; email: string | null }[]) {
        userNames[u.user_id] = u.display_name || u.email || String(u.user_id);
      }

      const list: MobileDocRow[] = rows.map((d) => ({
        document_id: d.document_id,
        version_number: d.version_number,
        is_active: d.is_active,
        target_entity_name: d.target_entity_name,
        target_record_id: d.target_record_id,
        original_name: d.original_name,
        file_type: d.file_type,
        file_size: d.file_size,
        storage_path: d.storage_path,
        upload_timestamp: d.upload_timestamp,
        uploaded_by_name:
          d.uploaded_by_user_id == null
            ? t('mobile.dms.system', 'System')
            : userNames[d.uploaded_by_user_id] ?? String(d.uploaded_by_user_id),
      }));
      setDocs(list);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run once on mount. `t` is intentionally omitted — it is a fresh identity
  // every render and would loop the effect (see MobileRoleListPage/MobileUserListPage).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return docs.filter((d) => {
      if (statusFacet === 'active' && d.is_active !== 'Y') return false;
      if (statusFacet === 'archived' && d.is_active !== 'N') return false;
      if (!q) return true;
      return (
        d.original_name.toLowerCase().includes(q) ||
        (d.file_type ?? '').toLowerCase().includes(q) ||
        d.target_entity_name.toLowerCase().includes(q) ||
        d.target_record_id.toLowerCase().includes(q) ||
        d.uploaded_by_name.toLowerCase().includes(q)
      );
    });
  }, [docs, filter, statusFacet]);

  const facets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.dms.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'archived', label: t('mobile.dms.archived', 'Archived') },
  ];

  const typeLabel = (d: MobileDocRow) => d.file_type ?? t('mobile.dms.unknown_type', 'Unknown type');

  const toggleActive = async (d: MobileDocRow) => {
    setActionBusy(true);
    setError(null);
    const next = d.is_active === 'Y' ? 'N' : 'Y';
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'dms_document', 'document_id', d.document_id, { is_active: next });
    setActionBusy(false);
    if (e) { setError(e.message); return; }
    setSelected(null);
    void reload();
  };

  const onView = async (d: MobileDocRow) => {
    setActionBusy(true);
    setError(null);
    try {
      const url = await getSignedUrl(d.storage_path);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.dms.title', 'Documents')}
        description={t('mobile.dms.description', 'Latest version of every attached document and its status.')}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.dms.search', 'Search documents')}
          aria-label={t('mobile.dms.search', 'Search documents')}
        />
      </div>

      <MobileFilterChips
        chips={facets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.dms.none', 'No documents match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((d) => (
            <MobileCardRow
              key={d.document_id}
              lead={<FileText />}
              leadTone={d.is_active === 'Y' ? 'blue' : 'slate'}
              title={d.original_name}
              subtitle={`${typeLabel(d)} · ${t('mobile.dms.version_short', 'v{{n}}', { n: d.version_number })} · ${d.target_entity_name}`}
              trailing={
                <MobileStatusPill tone={d.is_active === 'Y' ? 'ok' : 'no'}>
                  {d.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('mobile.dms.archived', 'Archived')}
                </MobileStatusPill>
              }
              chevron
              onClick={() => setSelected(d)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelected(null); }} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.dms.detail', 'Document detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><FileText size={20} /></span>
              <span>
                <div className="m-nm">{selected.original_name}</div>
                <div className="m-em">{typeLabel(selected)}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.dms.status', 'Status')}
                value={
                  <MobileStatusPill tone={selected.is_active === 'Y' ? 'ok' : 'no'}>
                    {selected.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('mobile.dms.archived', 'Archived')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow label={t('mobile.dms.version', 'Version')} value={t('mobile.dms.version_short', 'v{{n}}', { n: selected.version_number })} />
              <MobileDetailRow label={t('mobile.dms.size', 'Size')} value={formatBytes(selected.file_size)} />
              <MobileDetailRow
                label={t('mobile.dms.linked_to', 'Linked to')}
                /* fk-display-ok: polymorphic target pointer — a natural business identifier, not a surrogate FK. */
                value={`${selected.target_entity_name} · ${selected.target_record_id}`}
              />
              <MobileDetailRow label={t('mobile.dms.uploaded_by', 'Uploaded by')} value={selected.uploaded_by_name} />
              <MobileDetailRow label={t('mobile.dms.uploaded', 'Uploaded')} value={formatDateTime(selected.upload_timestamp)} />
            </MobileContentCard>
            <div className="m-actionbar">
              <button
                type="button"
                className="m-btn m-btn-primary"
                disabled={actionBusy}
                onClick={() => void onView(selected)}
              >
                <Eye size={16} />
                {t('mobile.dms.view', 'View')}
              </button>
              {canChange && (<button
                type="button"
                className={'m-btn ' + (selected.is_active === 'Y' ? 'm-btn-danger' : 'm-btn-ghost')}
                disabled={actionBusy}
                onClick={() => void toggleActive(selected)}
              >
                {selected.is_active === 'Y' ? <Archive size={16} /> : <ArchiveRestore size={16} />}
                {selected.is_active === 'Y' ? t('mobile.dms.archive', 'Archive') : t('mobile.dms.restore', 'Restore')}
              </button>)}
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
